import { resolveFinalUiAction } from '../../../../../apps/tutor-desk/plugins/_shared/final_ui_action_registry.js';

const TUTOR_DESK_SAAS_FINAL_UI_STANDARD = 'ttoria-saas-final-ui-v1';

// App-contributed Settings actions are owned by the Settings presentation
// adapter. They intentionally stay outside the Tutor Desk business-action
// Tooltip inventory: every control has a complete visible label, so the
// global Tooltip controller safely treats these unknown action IDs as
// ineligible while the Final UI roles, states, icons, and confirmations remain
// deterministic.
const SETTINGS_V2_FINAL_UI_ACTIONS = Object.freeze({
  'settings-v2-save': Object.freeze({ role: 'primary', icon: 'save', busyLabel: 'Saving…' }),
  'settings-v2-reset-tips': Object.freeze({ role: 'secondary', icon: 'refresh', busyLabel: 'Resetting…' }),
  'settings-v2-catalog-save': Object.freeze({ role: 'primary', icon: 'save', busyLabel: 'Saving…' }),
  'settings-v2-branding-upload': Object.freeze({ role: 'primary', icon: 'upload', busyLabel: 'Uploading…' }),
  'settings-v2-branding-publish': Object.freeze({ role: 'primary', icon: 'check', busyLabel: 'Publishing…' }),
  'settings-v2-branding-rollback': Object.freeze({
    role: 'danger',
    icon: 'undo',
    busyLabel: 'Restoring…',
    requiresDestructiveConfirmation: true
  }),
  'settings-v2-branding-archive': Object.freeze({
    role: 'danger',
    icon: 'archive',
    busyLabel: 'Archiving…',
    requiresDestructiveConfirmation: true
  })
});

export function isSettingsSaasFinalUiActive(doc = typeof document !== 'undefined' ? document : null) {
  const root = doc?.documentElement;
  if (!root || root.getAttribute('data-kg-ui-standard') !== TUTOR_DESK_SAAS_FINAL_UI_STANDARD) return false;
  return String(root.getAttribute('data-kg-ui-surfaces') || '')
    .trim()
    .split(/\s+/u)
    .includes('settings');
}

export function isSettingsReferenceV2Active(doc = typeof document !== 'undefined' ? document : null) {
  const root = doc?.documentElement;
  return isSettingsSaasFinalUiActive(doc)
    && root?.getAttribute('data-kg-runtime-target') === 'saas-staging'
    && root?.getAttribute('data-kg-settings-ui-profile') === 'reference-v2';
}

export function withSettingsPresentationMode(options = {}, doc = typeof document !== 'undefined' ? document : null) {
  const normalized = options && typeof options === 'object' && !Array.isArray(options)
    ? { ...options }
    : {};
  if (!isSettingsSaasFinalUiActive(doc)) return normalized;
  const busy = isBusySettingsAction(normalized);
  const disabled = isDisabledSettingsAction(normalized);
  if (!normalized.presentationMode) {
    normalized.presentationMode = 'class-contract';
  }
  if (busy) {
    normalized.busy = true;
    normalized.state = 'Loading';
  } else if (disabled) {
    normalized.state = 'Disabled';
  }
  const normalizedAttrs = getSettingsFinalUiActionAttributes({
    attrs: normalized.attrs,
    label: normalized.label,
    intent: normalized.intent,
    className: normalized.className,
    busy,
    disabled,
    state: normalized.state
  }, doc);
  if (normalized.attrs != null || Object.keys(normalizedAttrs).length > 0) {
    normalized.attrs = normalizedAttrs;
  }
  if (Array.isArray(normalized.tabs)) {
    normalized.tabs = normalized.tabs.map((tab) => {
      if (!tab || typeof tab !== 'object' || Array.isArray(tab)) return tab;
      return {
        ...tab,
        attrs: getSettingsFinalUiActionAttributes({
          attrs: tab.attrs,
          label: tab.label || tab.title,
          intent: 'Secondary',
          className: 'kg-settings-tab'
        }, doc)
      };
    });
  }
  return normalized;
}

export function resolveSettingsFinalUiAction(options = {}, doc = typeof document !== 'undefined' ? document : null) {
  if (!isSettingsSaasFinalUiActive(doc)) return null;
  const attrs = options && typeof options.attrs === 'object' && options.attrs
    ? options.attrs
    : {};
  if (!hasActionIdentifier(attrs)) return null;
  const settingsOwned = resolveSettingsV2OwnedFinalUiAction(options);
  if (settingsOwned) return settingsOwned;
  return resolveFinalUiAction({
    label: options.label,
    intent: options.intent,
    testId: attrs['data-testid'] || '',
    attrs,
    className: options.className,
    busy: isBusySettingsAction(options)
  });
}

export function getSettingsFinalUiActionAttributes({ attrs, label, intent, className, busy = false, disabled = false, state = '' } = {}, doc = typeof document !== 'undefined' ? document : null) {
  const normalizedAttrs = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
    ? { ...attrs }
    : {};
  if (!isSettingsSaasFinalUiActive(doc)) return normalizedAttrs;
  if (!hasActionIdentifier(normalizedAttrs)) return normalizedAttrs;
  const actionBusy = isBusySettingsAction({ busy, state, attrs: normalizedAttrs });
  const actionDisabled = isDisabledSettingsAction({ disabled, state, attrs: normalizedAttrs });
  const finalAction = resolveSettingsV2OwnedFinalUiAction({
    label,
    intent,
    attrs: normalizedAttrs,
    className,
    busy: actionBusy
  }) || resolveFinalUiAction({
    label,
    intent,
    testId: normalizedAttrs['data-testid'] || '',
    attrs: normalizedAttrs,
    className,
    busy: actionBusy
  });
  return {
    ...normalizedAttrs,
    ...(actionBusy ? { 'aria-busy': 'true' } : {}),
    'data-kg-action-id': String(finalAction.actionId || 'unclassified'),
    'data-kg-ui-role': 'action',
    'data-kg-ui-variant': String(finalAction.variant || finalAction.role || 'secondary'),
    'data-kg-ui-state': actionBusy ? 'busy' : actionDisabled ? 'disabled' : normalizeSettingsUiState(state),
    'data-kg-ui-classification': String(finalAction.classification || 'unclassified'),
    'data-kg-action-classification': String(finalAction.classification || 'unclassified'),
    'data-td-final-action-role': String(finalAction.role || 'secondary'),
    'data-td-final-action-size': String(finalAction.size || 'secondary'),
    'data-td-final-destructive-confirmation': finalAction.requiresDestructiveConfirmation ? 'required' : 'none'
  };
}

function resolveSettingsV2OwnedFinalUiAction(options = {}) {
  const attrs = options && typeof options.attrs === 'object' && options.attrs ? options.attrs : {};
  const identifier = String(attrs['data-action'] || '').trim();
  const definition = SETTINGS_V2_FINAL_UI_ACTIONS[identifier];
  if (!definition) return null;
  const busy = isBusySettingsAction(options);
  const role = definition.role;
  const size = role === 'primary' ? 'main' : 'secondary';
  return Object.freeze({
    standardId: TUTOR_DESK_SAAS_FINAL_UI_STANDARD,
    actionId: identifier,
    role,
    variant: role,
    size,
    label: String(options.label || '').trim(),
    canonicalLabel: String(options.label || '').trim(),
    labelPolicy: 'supplied',
    labelCase: 'title-case',
    icon: busy ? 'loader' : definition.icon,
    iconStyle: 'outlined',
    iconSize: 20,
    busyLabel: definition.busyLabel,
    permissionBehavior: 'preserve-existing-permission-policy',
    disabledBehavior: 'preserve-existing-disabled-and-aria-disabled',
    requiresDestructiveConfirmation: definition.requiresDestructiveConfirmation === true,
    confirmation: definition.requiresDestructiveConfirmation === true ? 'required' : 'none',
    placement: 'left',
    state: busy ? 'busy' : 'ready',
    intent: role === 'danger' ? 'Danger' : role === 'primary' ? 'Primary' : 'Secondary',
    classification: 'identifier',
    identifierClassified: true,
    identifier
  });
}

function hasActionIdentifier(attrs = {}) {
  return Boolean(String(attrs['data-action'] || attrs['data-kg-semantic-action-id'] || '').trim());
}

function isBusySettingsAction(options = {}) {
  const attrs = options && typeof options.attrs === 'object' && options.attrs ? options.attrs : {};
  return Boolean(options.busy)
    || String(options.state || '').trim().toLowerCase() === 'loading'
    || isTrueAttribute(attrs['aria-busy']);
}

function isDisabledSettingsAction(options = {}) {
  const attrs = options && typeof options.attrs === 'object' && options.attrs ? options.attrs : {};
  return Boolean(options.disabled)
    || String(options.state || '').trim().toLowerCase() === 'disabled'
    || isTrueAttribute(attrs['aria-disabled']);
}

function isTrueAttribute(value) {
  return value === true || String(value || '').trim().toLowerCase() === 'true';
}

function normalizeSettingsUiState(state = '') {
  const normalized = String(state || '').trim().toLowerCase();
  return ['hover', 'active', 'focus'].includes(normalized) ? normalized : 'default';
}
